import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, ChevronDown, MapPin, Home, Briefcase } from "lucide-react";
import { DonutBreakdown } from "./visitor-id/DonutBreakdown";
import { combineVisitorRecords } from "./visitor-id/combineRecords";

/**
 * VisitorIdStoryCard — client-facing chapter card that turns the Website
 * Visitor ID PDF into a "who's curious about your business" narrative.
 *
 * Client view only shows AGGREGATES (per user preference — no individual
 * PII). Shows:
 *   • 3 hero KPIs (addresses, verified emails, businesses)
 *   • Age Range donut breakdown
 *   • Net Worth donut breakdown
 *   • Home Value bar breakdown (relevant for remodeling clients)
 *   • Top Industries chips (from business visitors)
 *   • Top Pages Visited list
 *
 * Diplomatic tone: growth in lime, dip in amber, never red.
 */

const fmt = (n) => {
  if (n == null || isNaN(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return (n / 1_000).toFixed(1) + "K";
  if (n >= 1_000) return n.toLocaleString();
  return String(Math.round(n));
};

const fmtPeriodLabel = (start, end) => {
  if (!start || !end) return "-";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  try {
    const s = start.split("-");
    const e = end.split("-");
    const sM = months[parseInt(s[1], 10) - 1];
    const eM = months[parseInt(e[1], 10) - 1];
    if (s[0] === e[0] && s[1] === e[1]) return `${sM} ${s[0]}`;
    if (s[0] === e[0]) return `${sM} to ${eM} ${e[0]}`;
    return `${sM} ${s[0]} to ${eM} ${e[0]}`;
  } catch { return `${start} to ${end}`; }
};


const DeltaPill = ({ delta }) => {
  if (!delta) return null;
  if (delta.isNew) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums bg-lime-400/15 border border-lime-400/30 text-lime-300">NEW</span>
    );
  }
  if (delta.direction === "flat") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tabular-nums bg-slate-700/40 border border-slate-600/40 text-slate-400">flat</span>
    );
  }
  const up = delta.direction === "up";
  const cls = up
    ? "bg-lime-400/15 border-lime-400/30 text-lime-300"
    : "bg-amber-400/10 border-amber-400/25 text-amber-300";
  const arrow = up ? "▲" : "▼";
  const display = Math.abs(delta.pct) >= 100
    ? `${up ? "+" : "−"}${Math.round(Math.abs(delta.pct))}%`
    : `${up ? "+" : "−"}${Math.abs(delta.pct).toFixed(Math.abs(delta.pct) >= 10 ? 0 : 1)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums border ${cls}`}>
      <span className="text-[8px]">{arrow}</span>{display}
    </span>
  );
};

const KPI = ({ label, value, delta, icon: Icon, testId }) => (
  <div className="bg-slate-900/50 border border-emerald-400/15 rounded-xl p-3.5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-3.5 h-3.5 text-emerald-300" />}
      <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">{label}</p>
    </div>
    <div className="flex items-baseline gap-2">
      <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">{value}</p>
      <DeltaPill delta={delta} />
    </div>
  </div>
);

// Ordering helper for age range buckets so the chart reads left→right.
const AGE_ORDER = ["<18", "18-22", "23-29", "30-39", "40-49", "50-59", "60-69", "70-79", "80-89", ">89", "Unspecified"];
const sortByBucketOrder = (items, order) => {
  const idx = (r) => {
    const i = order.indexOf(r);
    return i === -1 ? 999 : i;
  };
  return [...items].sort((a, b) => idx(a.range) - idx(b.range));
};

// Sort net worth / home value buckets by their leading number magnitude.
const parseLeadingMagnitude = (label) => {
  if (!label) return -1;
  const s = String(label).replace(/\s/g, "");
  if (s.startsWith("<")) return 0;
  if (s.startsWith(">")) return 1e12;
  if (/unspecified/i.test(s)) return 1e13;
  const m = s.match(/^(\d+(?:\.\d+)?)([KMB]?)/i);
  if (!m) return -1;
  const n = parseFloat(m[1]);
  const mult = { "": 1, K: 1e3, M: 1e6, B: 1e9 }[m[2].toUpperCase()] || 1;
  return n * mult;
};

// Roll many small buckets into a few readable groups, by each bucket's lower bound
const NET_WORTH_GROUPS = [
  { label: "Under 100K", max: 100e3 }, { label: "100K-250K", max: 250e3 }, { label: "250K-500K", max: 500e3 },
  { label: "500K-1M", max: 1e6 }, { label: "Over 1M", max: Infinity },
];
const HOME_VALUE_GROUPS = [
  { label: "Under 500K", max: 500e3 }, { label: "500K-1M", max: 1e6 }, { label: "Over 1M", max: Infinity },
];
const groupBuckets = (items, groups) => {
  const totals = groups.map((g) => ({ range: g.label, pct: 0 }));
  let unspecified = 0;
  for (const it of items || []) {
    const pct = Number(it.pct) || 0;
    if (/unspecified/i.test(it.range || "")) { unspecified += pct; continue; }
    const v = parseLeadingMagnitude(it.range);
    const i = groups.findIndex((g) => v < g.max);
    if (i >= 0) totals[i].pct += pct;
  }
  if (unspecified > 0) totals.push({ range: "Unspecified", pct: unspecified });
  return totals.filter((t) => t.pct > 0);
};


const shortenUrl = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname + (u.search || "");
    return path === "/" ? "Home page" : path;
  } catch {
    return url;
  }
};

export default function VisitorIdStoryCard({ records = [], clientName = "Your business" }) {
  const [isOpen, setIsOpen] = useState(false);
  const rec = useMemo(() => combineVisitorRecords(records), [records]);
  if (!rec) return null;

  const ageSorted = sortByBucketOrder(rec.age_range || [], AGE_ORDER);
  const netWorthSorted = groupBuckets(rec.net_worth, NET_WORTH_GROUPS);
  const homeValueSorted = groupBuckets(rec.home_value, HOME_VALUE_GROUPS);

  const topIndustries = (rec.top_industries || []).slice(0, 6);
  const topPages = (rec.top_pages || []).slice(0, 5);

  const periodLabel = fmtPeriodLabel(rec.start, rec.end);

  return (
    <section
      className="bg-gradient-to-br from-emerald-500/10 via-teal-500/[0.06] to-sky-500/[0.08] rounded-2xl p-5 md:p-6 border border-emerald-400/25 mb-8"
      data-testid="visitor-id-story-section"
    >
      {/* Collapsed header */}
      <div
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen((v) => !v); } }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        data-testid="visitor-id-toggle"
        className="flex items-center gap-3 cursor-pointer select-none group"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-[0.18em]">
            Website Visitor ID
          </p>
          <p className="text-white text-base md:text-lg font-bold leading-snug" data-testid="visitor-id-title">
            Here's what we know about your website visitors.
          </p>
          <p className="text-slate-300 text-[12px] mt-0.5">
            {fmt(rec.addresses_captured)} real homeowners quietly explored {clientName}.
          </p>
          <p className="text-slate-400 text-[11px] mt-0.5 tabular-nums">{periodLabel}</p>
          <p className="text-emerald-300/70 text-[11px] mt-0.5 group-hover:opacity-100 opacity-80 transition-opacity">
            {isOpen ? "Click to collapse" : "Click to expand"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-medium rounded-full border border-emerald-400/30">
            {rec.verified_emails} verified emails
          </span>
          <ChevronDown className={`w-5 h-5 text-emerald-300 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="visitor-id-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-5">
              {/* Sub-headline: running totals across every report */}
              <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <p className="text-slate-400 text-sm">
                  Who&apos;s been checking out {clientName}&apos;s website. Real homeowners, real profiles, quiet interest.
                </p>
                <span className="px-2.5 py-1 rounded-lg bg-slate-800 border border-emerald-400/30 text-white text-sm tabular-nums" data-testid="visitor-id-range">
                  {periodLabel} · running total
                </span>
              </div>

              {/* Hero KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <KPI label="Homeowners Identified" value={fmt(rec.addresses_captured)} icon={MapPin} testId="vid-kpi-addresses" />
                <KPI label="Verified Emails" value={fmt(rec.verified_emails)} icon={Users} testId="vid-kpi-emails" />
                <KPI label="Businesses" value={fmt(rec.businesses_captured)} icon={Briefcase} testId="vid-kpi-businesses" />
              </div>

              {/* Narrative callout */}
              {rec.addresses_captured > 0 && (
                <div className="bg-emerald-500/8 border border-emerald-400/25 rounded-xl p-4 mb-6" data-testid="vid-narrative">
                  <p className="text-slate-200 text-sm leading-relaxed">
                    <span className="text-white font-bold">{fmt(rec.addresses_captured)} real homeowners</span> have quietly explored {clientName} so far.{" "}
                    {rec.verified_emails > 0 && (
                      <>
                        <span className="text-lime-300 font-semibold">{rec.verified_emails}</span> shared verified emails: a warm list ready for outreach.{" "}
                      </>
                    )}
                    {rec.businesses_captured > 0 && (
                      <>Plus <span className="text-cyan-300 font-semibold">{rec.businesses_captured}</span> businesses looked at your work.</>
                    )}
                  </p>
                </div>
              )}

              {/* Demographic breakdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                <DonutBreakdown title="Who They Are: Age" icon={Users} items={ageSorted} testId="vid-age-breakdown" />
                <DonutBreakdown title="Net Worth" icon={Briefcase} items={netWorthSorted} testId="vid-networth-breakdown" />
                <DonutBreakdown title="Their Homes: Value" icon={Home} items={homeValueSorted} testId="vid-homevalue-breakdown" />
              </div>

              {/* Top Industries + Top Pages side-by-side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {topIndustries.length > 0 && (
                  <div className="bg-slate-900/40 border border-emerald-400/15 rounded-xl p-4" data-testid="vid-industries">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-3.5 h-3.5 text-emerald-300" />
                      <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">Top Business Visitors</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {topIndustries.map((it, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 text-xs"
                          data-testid={`vid-industry-chip-${i}`}
                        >
                          {it.industry}
                          {it.count > 1 && (
                            <span className="text-[10px] font-bold text-emerald-300 tabular-nums">×{it.count}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {topPages.length > 0 && (
                  <div className="bg-slate-900/40 border border-emerald-400/15 rounded-xl p-4" data-testid="vid-top-pages">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-3.5 h-3.5 text-emerald-300" />
                      <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">Top Pages Visited</p>
                    </div>
                    <div className="space-y-1.5">
                      {topPages.map((it, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid={`vid-page-row-${i}`}>
                          <span className="text-slate-200 truncate flex-1">{shortenUrl(it.url)}</span>
                          <span className="text-emerald-300 tabular-nums font-semibold flex-shrink-0">{it.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footnote */}
              <p
                className="mt-5 pt-4 border-t border-emerald-400/10 text-slate-400 text-[11px] italic leading-relaxed"
                data-testid="visitor-id-footnote"
              >
                Visitor ID pairs anonymous website traffic with public homeowner profiles. Every number
                above represents a real household. Verified emails have been cross-checked against
                third-party data sources. Individual contact details are held by your agency and available on request.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
